import React from "react";
import { Plus } from "lucide-react";

// ─── Spinner ──────────────────────────────────────────────────────────────────
export const Spinner = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg className={`animate-spin ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
  </svg>
);

// ─── Shared field classes (import these in any inline-form page) ──────────────
export const FIELD_BASE =
  "shared-input-field bg-gray-50 border text-gray-900 text-sm rounded-lg block w-full p-2.5 " +
  "dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white " +
  "focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-500 dark:focus:border-blue-500 " +
  "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 " +
  "dark:disabled:bg-gray-800 dark:disabled:text-gray-500";

export const FIELD_ERROR =
  "bg-red-50 border-error text-red-900 placeholder-red-700 " +
  "focus:ring-error focus:border-error dark:text-error dark:placeholder-red-500 dark:border-error";

export const fieldCls = (hasError: boolean) =>
  `${FIELD_BASE} ${hasError ? FIELD_ERROR : "border-gray-300"}`;

// ─── Column definition ────────────────────────────────────────────────────────
export interface TableColumn {
  key: string;
  header: string;
  width?: number;
}

// ─── InlineTable ──────────────────────────────────────────────────────────────
interface InlineTableProps {
  columns: TableColumn[];
  children?: React.ReactNode;
  loading?: boolean;
  emptyText?: string;
  emptySubText?: string;
  minWidth?: number;
  className?: string;
}

const InlineTable: React.FC<InlineTableProps> = ({
  columns,
  children,
  loading = false,
  emptyText = "No values yet",
  emptySubText = "Add your first value using the form below",
  minWidth = 600,
  className = "",
}) => {
  const colCount = columns.length;
  const hasRows = React.Children.count(children) > 0;

  return (
    <div className={`flex-1 min-h-0 overflow-auto ${className}`}>
      <table style={{ tableLayout: "fixed", width: "100%", minWidth }}>
        <colgroup>
          {columns.map((col) => (
            <col key={col.key} style={col.width !== undefined ? { width: col.width } : undefined} />
          ))}
        </colgroup>
        <thead className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-y border-gray-100 dark:border-gray-700">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide whitespace-nowrap"
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
          {loading ? (
            <tr>
              <td colSpan={colCount}>
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-gray-400">
                  <Spinner className="h-7 w-7 text-blue-500" />
                  <span className="text-sm">Loading…</span>
                </div>
              </td>
            </tr>
          ) : !hasRows ? (
            <tr>
              <td colSpan={colCount}>
                <div className="flex flex-col items-center justify-center gap-2 py-16">
                  <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-1">
                    <Plus size={22} className="text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{emptyText}</p>
                  {emptySubText && <p className="text-xs text-gray-400">{emptySubText}</p>}
                </div>
              </td>
            </tr>
          ) : (
            children
          )}
        </tbody>
      </table>
    </div>
  );
};

export default InlineTable;
